// Datenzugriffe des internen Chats. Getrennt von nachrichten.ts, damit die reinen
// Helfer dort ohne Supabase testbar bleiben.
import { supabase } from '../supabase/client';
import type { ChatAnhang, ChatGruppe, ChatNachricht, Kollege } from './nachrichten';
import { CHAT_ROLLEN, nimmtAmChatTeil } from './teilnehmer';

const BUCKET = 'chat-bilder';

const SPALTEN = 'id, absender_id, empfaenger_id, gruppe_id, text, bild_path, bild_name, anhang, created_at, gelesen_am';

/**
 * Alle aktiven Mitarbeiter der Belegschaft außer mir selbst — die Regel steht in
 * teilnehmer.ts. Die Rollen grenzt schon die Abfrage ein (weniger Zeilen über die
 * Leitung), den Rest entscheidet dieselbe Funktion, die auch die Tests prüfen.
 */
export async function ladeKollegen(meineId: string): Promise<Kollege[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, email, avatar_url, status, role, ist_angestellt')
    .neq('id', meineId)
    .in('role', [...CHAT_ROLLEN])
    .order('full_name');
  if (error) { console.error('Kollegen laden fehlgeschlagen', error.message); return []; }
  return (data || [])
    .filter(nimmtAmChatTeil)
    .map(({ id, full_name, email, avatar_url }: Kollege) => ({ id, full_name, email, avatar_url }));
}

/**
 * Der komplette eigene Chat. Das ist bewusst EIN Abruf ohne Paginierung: der Bestand wird
 * wöchentlich geleert (Cron), es liegen also nie mehr als ein paar Tage darin.
 */
export async function ladeNachrichten(meineId: string): Promise<ChatNachricht[]> {
  const { data, error } = await supabase
    .from('chat_nachrichten')
    .select(SPALTEN)
    // 1:1 nur meine Zeilen, Gruppen filtert RLS (ist_chat_mitglied)
    .or(`absender_id.eq.${meineId},empfaenger_id.eq.${meineId},gruppe_id.not.is.null`)
    .order('created_at', { ascending: false })
    .limit(2000);
  if (error) { console.error('Chat laden fehlgeschlagen', error.message); return []; }
  return (data || []) as ChatNachricht[];
}

export async function sendeNachricht(args: {
  absenderId: string;
  empfaengerId?: string | null;
  gruppeId?: string | null;
  text: string;
  bildPath?: string | null;
  bildName?: string | null;
  anhang?: ChatAnhang | null;
}): Promise<ChatNachricht | null> {
  const { data, error } = await supabase
    .from('chat_nachrichten')
    .insert({
      absender_id: args.absenderId,
      empfaenger_id: args.gruppeId ? null : args.empfaengerId,
      gruppe_id: args.gruppeId || null,
      text: args.text.trim() || null,
      bild_path: args.bildPath || null,
      bild_name: args.bildName || null,
      anhang: args.anhang || null,
    })
    .select(SPALTEN)
    .single();
  if (error) { console.error('Senden fehlgeschlagen', error.message); return null; }
  return data as ChatNachricht;
}

/** Alles von diesem Gegenüber als gelesen stempeln (nur eingehende, nur ungelesene, keine Gruppenzeilen). */
export async function markiereGelesen(meineId: string, partnerId: string): Promise<void> {
  const { error } = await supabase
    .from('chat_nachrichten')
    .update({ gelesen_am: new Date().toISOString() })
    .eq('empfaenger_id', meineId)
    .eq('absender_id', partnerId)
    .is('gelesen_am', null)
    .is('gruppe_id', null);
  if (error) console.error('Lesestempel fehlgeschlagen', error.message);
}

/** Eigene Nachricht zurücknehmen — das Bild wandert gleich mit. */
export async function loescheNachricht(n: ChatNachricht): Promise<boolean> {
  if (n.bild_path) await supabase.storage.from(BUCKET).remove([n.bild_path]);
  const { error } = await supabase.from('chat_nachrichten').delete().eq('id', n.id);
  if (error) { console.error('Löschen fehlgeschlagen', error.message); return false; }
  return true;
}

export const MAX_BILD_BYTES = 10 * 1024 * 1024;

/** Erlaubte Nicht-Bild-Dateien im Chat (Endung → MIME), falls der Browser keinen Typ liefert. */
const MIME_NACH_ENDUNG: Record<string, string> = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp', heic: 'image/heic',
  pdf: 'application/pdf', doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  odt: 'application/vnd.oasis.opendocument.text', ods: 'application/vnd.oasis.opendocument.spreadsheet',
  txt: 'text/plain', csv: 'text/csv',
};

/**
 * Bild in den eigenen Ordner legen; der Pfad wandert danach in die Nachricht.
 * Gibt im Fehlerfall einen ANZEIGBAREN Grund zurück — ein still gescheiterter Upload
 * sieht sonst aus, als würde der Knopf nichts tun.
 */
export async function ladeBildHoch(
  meineId: string,
  datei: File,
): Promise<{ ok: true; path: string; name: string } | { ok: false; fehler: string }> {
  if (datei.size > MAX_BILD_BYTES) {
    return { ok: false, fehler: `Die Datei ist ${(datei.size / 1024 / 1024).toFixed(1)} MB groß — erlaubt sind 10 MB.` };
  }
  const endung = (datei.name.split('.').pop() || 'jpg').toLowerCase().slice(0, 5);
  const typ = datei.type || MIME_NACH_ENDUNG[endung];
  // #549: neben Bildern auch PDFs und Office-Dateien (Bucket-Freigabe in 2026-10-22-chat-unterlagen.sql).
  if (!typ || !(typ.startsWith('image/') || Object.values(MIME_NACH_ENDUNG).includes(typ))) {
    return { ok: false, fehler: 'Dieser Dateityp geht im Chat nicht — erlaubt sind Bilder, PDF, Word, Excel und Text.' };
  }
  const path = `${meineId}/${crypto.randomUUID()}.${endung}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, datei, { contentType: typ });
  if (error) {
    console.error('Datei-Upload fehlgeschlagen', error.message);
    return { ok: false, fehler: `Datei konnte nicht hochgeladen werden: ${error.message}` };
  }
  return { ok: true, path, name: datei.name };
}

/**
 * Signierte URL zum Anzeigen (der Bucket ist privat).
 * Mit `dateiname` liefert Storage die Datei als Download aus (Content-Disposition
 * attachment) statt sie im Tab zu öffnen.
 */
export async function bildUrl(path: string, dateiname?: string | null): Promise<string | null> {
  const { data, error } = await supabase.storage.from(BUCKET)
    .createSignedUrl(path, 3600, dateiname ? { download: dateiname } : undefined);
  if (error || !data?.signedUrl) { console.error('Bild-Link fehlgeschlagen', error?.message); return null; }
  return data.signedUrl;
}

/**
 * Meine Gruppen samt Mitgliedern und Lesestand. Drei Abrufe statt eines Embeds:
 * Team-Mitglieder und Gruppen-Mitglieder liegen in verschiedenen Tabellen, und
 * PostgREST kann sie nicht in einem Zug zusammenführen.
 */
export async function ladeGruppen(meineId: string): Promise<ChatGruppe[]> {
  const { data: gruppen, error } = await supabase
    .from('chat_gruppen')
    .select('id, name, team_id, erstellt_von, teams(name)');
  if (error) { console.error('Gruppen laden fehlgeschlagen', error.message); return []; }
  const ids = (gruppen || []).map((g: { id: string }) => g.id);
  if (!ids.length) return [];

  const [mitgliederQ, teamsQ, gelesenQ] = await Promise.all([
    supabase.from('chat_gruppen_mitglieder').select('gruppe_id, profile_id').in('gruppe_id', ids),
    supabase.from('team_members').select('team_id, profile_id'),
    supabase.from('chat_gruppen_gelesen').select('gruppe_id, gelesen_bis').eq('profile_id', meineId),
  ]);

  const proGruppe = new Map<string, string[]>();
  for (const m of mitgliederQ.data || []) {
    proGruppe.set(m.gruppe_id, [...(proGruppe.get(m.gruppe_id) || []), m.profile_id]);
  }
  const proTeam = new Map<string, string[]>();
  for (const m of teamsQ.data || []) {
    proTeam.set(m.team_id, [...(proTeam.get(m.team_id) || []), m.profile_id]);
  }
  const stand = new Map((gelesenQ.data || []).map((z: { gruppe_id: string; gelesen_bis: string }) => [z.gruppe_id, z.gelesen_bis]));

  return (gruppen || []).map((g: any) => ({
    id: g.id,
    name: g.name,
    team_id: g.team_id,
    team_name: g.teams?.name ?? null,
    erstellt_von: g.erstellt_von,
    mitglieder: g.team_id ? (proTeam.get(g.team_id) || []) : (proGruppe.get(g.id) || []),
    gelesen_bis: stand.get(g.id) ?? null,
  }));
}

/** Lesestand auf jetzt setzen (eine Zeile je Person und Gruppe). */
export async function setzeGruppenLesestand(meineId: string, gruppeId: string): Promise<void> {
  const { error } = await supabase.from('chat_gruppen_gelesen')
    .upsert({ gruppe_id: gruppeId, profile_id: meineId, gelesen_bis: new Date().toISOString() },
            { onConflict: 'gruppe_id,profile_id' });
  if (error) console.error('Lesestand fehlgeschlagen', error.message);
}

/** Freie Gruppe gründen — die Gründerin ist immer selbst dabei. */
export async function gruendeGruppe(name: string, meineId: string, mitglieder: string[]): Promise<ChatGruppe | null> {
  const { data, error } = await supabase.from('chat_gruppen')
    .insert({ name: name.trim(), erstellt_von: meineId })
    .select('id, name, team_id, erstellt_von').single();
  if (error || !data) { console.error('Gruppe anlegen fehlgeschlagen', error?.message); return null; }
  const alle = [...new Set([meineId, ...mitglieder])];
  const { error: mErr } = await supabase.from('chat_gruppen_mitglieder')
    .insert(alle.map((profile_id) => ({ gruppe_id: data.id, profile_id })));
  if (mErr) {
    console.error('Mitglieder anlegen fehlgeschlagen', mErr.message);
    // Ohne Mitglieder ist die Gruppe für niemanden sichtbar (RLS über ist_chat_mitglied) —
    // sie bliebe sonst als unsichtbarer Datenmüll liegen. Deshalb wieder entfernen.
    await supabase.from('chat_gruppen').delete().eq('id', data.id);
    return null;
  }
  return { ...data, team_name: null, mitglieder: alle, gelesen_bis: null } as ChatGruppe;
}

export async function benenneGruppeUm(gruppeId: string, name: string): Promise<boolean> {
  const { error } = await supabase.from('chat_gruppen').update({ name: name.trim() }).eq('id', gruppeId);
  if (error) { console.error('Umbenennen fehlgeschlagen', error.message); return false; }
  return true;
}

export async function fuegeMitgliedHinzu(gruppeId: string, profileId: string): Promise<boolean> {
  const { error } = await supabase.from('chat_gruppen_mitglieder')
    .insert({ gruppe_id: gruppeId, profile_id: profileId });
  if (error) { console.error('Mitglied hinzufuegen fehlgeschlagen', error.message); return false; }
  return true;
}

/** Auch der Austritt: profile_id ist dann die eigene ID. */
export async function entferneMitglied(gruppeId: string, profileId: string): Promise<boolean> {
  // Team-Chats beziehen ihre Mitglieder aus team_members, nicht aus chat_gruppen_mitglieder.
  // Ein DELETE ohne Treffer ist kein Fehler — ohne diese Prüfung würde die Funktion also
  // "true" melden, obwohl gar nichts passiert ist. Deshalb vorab klar scheitern lassen.
  const { data: gruppe, error: gErr } = await supabase.from('chat_gruppen')
    .select('team_id').eq('id', gruppeId).single();
  if (gErr) { console.error('Gruppe pruefen fehlgeschlagen', gErr.message); return false; }
  if (gruppe?.team_id) {
    console.error('Mitglied entfernen fehlgeschlagen: Team-Chat bezieht seine Mitglieder aus dem Team');
    return false;
  }
  const { error } = await supabase.from('chat_gruppen_mitglieder')
    .delete().eq('gruppe_id', gruppeId).eq('profile_id', profileId);
  if (error) { console.error('Mitglied entfernen fehlgeschlagen', error.message); return false; }
  return true;
}
